import type { TFunction } from "i18next";

/**
 * Agreed maximum lengths for user-facing text fields (AKL-728).
 *
 * These mirror the API's `src/domains/field_limits.py`. The authority for both is
 * `docs/field_length_limits.md` in akili.admin.api — change a number in all three.
 *
 * Two things worth knowing:
 *
 * - Wildlife Insights is a floor, never a ceiling. Their Batch Upload Data Dictionary
 *   states a length for exactly three project fields (project_name 400,
 *   project_short_name 43, project_admin_email 255) and leaves everything else unbounded
 *   free text. A form limit below a WI number means an import truncates or fails.
 * - Narrative fields are stored unbounded (`TEXT`) precisely so no import can truncate.
 *   NARRATIVE is an edge guard against someone pasting a blob, not a storage limit, and
 *   the API rejects the same number with a 422.
 */
export const FIELD_LIMITS = {
  /** WI states 400 for project_name. */
  PROJECT_NAME: 400,
  /** WI states 43, and uses short name as the project_id on batch upload. */
  SHORT_NAME: 43,
  /** WI states 255 for project_admin_email. */
  EMAIL: 255,
  /** No external standard. International formats run past the old 20-char column. */
  PHONE: 50,
  /** Narrative free text: unbounded in the database, guarded here and at the API. */
  NARRATIVE: 10000,
  /** WI free text that is a single-line input here ("list the strata"). */
  LONG_LINE: 2000,
  /** The postal address column has always been 400. */
  ADDRESS: 400,
  /** Everything with no reason to be anything else. */
  TEXT: 255,
} as const;

/**
 * Format a length for display in the current language.
 *
 * The counter and the over-length message both show raw counts, so they need grouping
 * separators that match the UI language — `10,000` in English, `10.000` in German.
 */
export const formatLength = (value: number, language?: string): string =>
  value.toLocaleString(language || undefined);

/**
 * The over-length message for one field, or `undefined` when it fits.
 *
 * The limit is interpolated rather than written into the translated string. That is
 * deliberate: the organization form's old "Email length exceeds 255 character limit"
 * said 255 while the code checked 50, and a number baked into copy drifts the moment
 * the check changes.
 */
export const lengthError = (
  t: TFunction,
  label: string,
  value: string | null | undefined,
  limit: number,
  language?: string,
): string | undefined => {
  const length = value?.length ?? 0;
  if (length <= limit) return undefined;
  return t("{{field}} is {{over}} characters over the {{limit}} limit", {
    field: label,
    over: formatLength(length - limit, language),
    limit: formatLength(limit, language),
  });
};
